/**
 * Composer 템플릿 기반 보고서 작성 페이지 (Phase 4a-2, 최소 슬라이스).
 *
 * 플로우:
 * 1. URL: /reports/new/composed/:templateKey (?inspectionId= 또는 ?inspectionIds=a,b 점검 컨텍스트)
 * 2. mount 시 템플릿 로드 → 작성 노드 = `deriveWriteForm(tpl)`(본문 바인딩 파생 = 골격의 단일 출처)
 * 2-b. 렌더·검증이 읽는 정본은 `writeTree` 하나 — 저작 트리(`composerWidgetTree`)가 있으면 그것,
 *      없으면 2의 파생 노드. 작성 폼 렌더러도 하나다(`ComposerWidgetTreeRenderer`, 2026-09-30 S3-b).
 * 3. 제목 + 현장 선택 + 폼 입력 → content(values) 수집
 * 4. POST /reports/composed (inspectionIds 포함) → 성공 시 상세(/reports/:id)로 이동
 *
 * 스냅샷 박제는 서버 createComposedReport가 수행(상세는 report.templateSnapshot 우선 렌더).
 * 진입점 카드 통합(ReportTypeSelectModal / reports/new.tsx)은 Phase B — 여기선 query 수용만.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  deriveWriteForm,
  useActiveComposerView,
  ComposeWritePageShell,
  applyVariableDynamicOptions,
  collectMissingRequired,
  collectSortableSelectOptionSources,
  collectWidgetDefaultValues,
  focusMissingRequiredField,
  mergeMethodReresolve,
  missingRequiredToastMessage,
  collectCatalogParamVariableKeys,
  methodTriggerSignature,
  resolveFindingRiskMethodValue,
} from '@safetyhub/report-composer';
import type { ComposeChromeButtonProps } from '@safetyhub/report-composer';
import type {
  ComposeChromeItem,
  ComposeChromeSlot,
  ComposerWidget,
  ComposeSubmitSituation,
  ImprovementCandidatePart,
  InputWidget,
  ReportComposerPreviewContext,
  ReportTemplate,
} from '@safetyhub/shared';
import {
  COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS,
  isSuggestionDictKey,
  resolveComposeSubmitLabel,
} from '@safetyhub/shared';
import { useAuthStore } from '../../stores/auth';
import { useToastStore } from '../../stores/toast';
import { extractApiError } from '../../lib/api';
import { sitesApi, Site } from '../../lib/organizations';
import {
  createComposedReport,
  generateImprovementCandidates,
  getReport,
  updateReport,
  editReport,
  canEditReport,
  uploadReportFile,
  type Report,
  type ReportContent,
} from '../../lib/reports';
import {
  getRenderTemplate,
  getComposerPrefill,
  submitComposerCatalogRow,
} from '../../lib/report-composer';
import { ComposerWidgetTreeRenderer } from '../../components/reports/composer/WriteFormRenderer';
import { COMPOSED_ROW_WIDGETS } from '@safetyhub/report-composer';
import { COMPOSED_FORM_WIDGETS } from '../../components/reports/composer/registeredFormWidgets';
import { ComposePreviewView } from '../../components/reports/composer/ComposePreviewView';
import {
  improvementCandidatesToRows,
  composedEntryGuidanceKey,
} from '../../components/reports/composer/improvementCandidates';
import { Card, ConfirmModal } from '@safetyhub/ui';
import { useStickyChromeTop } from '../../hooks/useStickyChromeTop';
import { useComposerSuggestHandlers } from '../../hooks/useComposerSuggestHandlers';
import { useComposerPhotoAnalysis } from '../../hooks/useComposerPhotoAnalysis';
import { useComposerImprovementImageHost } from '../../hooks/useComposerImprovementImageHost';
import { useSlotSignatures } from '../../hooks/useSlotSignatures';
import { SignatureModal } from '../../components/common';
import { extractListRowsFromPrefill } from './composedPrefillRows';
import { resolveStoredFindingRiskMethod } from './composedStoredRiskMethod';
import { editReportPrefillContext, resolveMethodReresolveRequest } from './composedEditContext';
import { SiblingReportChips } from '../../components/reports/SiblingReportChips';
import { useSiblingReports } from '../../components/reports/useSiblingReports';
import { isSiblingAwaitingWrite, resolveSiblingWriteHref } from './siblingWriteHref';
import {
  isPostSubmitReviewTraversal,
  resolveWriteScreenSiblingHref,
  templatePostSubmitFlow,
} from './postSubmitFlows';

export default function NewComposed() {
  const navigate = useNavigate();
  const { templateKey } = useParams<{ templateKey: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { currentMembership, user } = useAuthStore();
  const { showToast } = useToastStore();

  const companyId = currentMembership?.companyId || '';
  // 검색 제안(suggestionsFrom) 호스트 배선 — companyId 없으면 미배선으로 degrade(제안 없이 입력).
  const { onSuggestSearch, onSuggestSelect } = useComposerSuggestHandlers(companyId);
  // 개선 이미지 AI(등록형 행 위젯 improvement-image) 호스트 배선 — companyId 없으면 미배선(표시 전용).
  const improvementImageHost = useComposerImprovementImageHost(companyId);

  // 점검 컨텍스트(진입점에서 query로 전달). 다중(?inspectionIds=a,b,c 콤마) 또는 단일(?inspectionId=) 수용.
  // Phase C: 문서관리 작성 흐름이 다중 점검을 inspectionIds로 전달한다.
  const inspectionId = searchParams.get('inspectionId');
  const inspectionIdsParam = searchParams.get('inspectionIds');
  const inspectionIds = useMemo(
    () =>
      inspectionIdsParam
        ? inspectionIdsParam.split(',').map((s) => s.trim()).filter(Boolean)
        : inspectionId
          ? [inspectionId]
          : undefined,
    [inspectionIdsParam, inspectionId],
  );
  // 진입점에서 점검의 현장이 결정되어 함께 넘어온 경우 현장 SELECT prefill
  const siteIdParam = searchParams.get('siteId');

  // 수정(edit) 모드 — DRAFT/REJECTED COMPOSED 보고서를 이 생성 페이지 재사용으로 편집(구 SMS ?editReportId= 동형).
  // 편집 시 생성 전용 seeding(prefill 값 채널/AI후보)은 건너뛰고, 보고서 content로 폼을 seed한다.
  // 평가 방식 변경 재resolve는 편집에서도 돈다(아래 effect — 컨텍스트만 문서에서, 병합은 현재 행 기준).
  const editReportId = searchParams.get('editReportId') || '';
  const isEditMode = !!editReportId;

  // 서명 슬롯 수집(P2) — **편집 모드에서만** 배선한다: 서명 주소 (reportId, slotKey, rowId)의
  // reportId가 생성 모드엔 아직 없다(저장 후 수정 화면에서 걷는 흐름 — 설계 §0). 미배선이면
  // rc가 표시 전용 빈 칸으로 degrade(탭 무동작 — 캔버스와 같은 모양).
  const slotSig = useSlotSignatures({
    reportId: isEditMode ? editReportId : undefined,
    companyId: companyId || undefined,
    onError: (m) => showToast(m, 'error'),
  });

  // 뒤로가기 목적지 라벨(구 SMS 작성 페이지 규칙) — 진입점이 state.fromLabel로 명시하면 그것,
  // 없으면 점검 컨텍스트='점검 상세'(점검 상세 모달 경유) / 그 외 '새 보고서 작성'(new.tsx 경유).
  const entryStateRef = useRef<{ from?: string; fromLabel?: string } | null>(null);
  if (entryStateRef.current === null) {
    entryStateRef.current = (location.state as { from?: string; fromLabel?: string } | null) ?? {};
  }
  const backLabel =
    entryStateRef.current?.fromLabel || (inspectionIds?.length ? '점검 상세' : '새 보고서 작성');

  // prefill resolve 컨텍스트 — 안정적 참조로 불필요한 재조회 방지.
  const suggestContext = useMemo<ReportComposerPreviewContext>(
    () => ({
      companyId: companyId || undefined,
      siteId: siteIdParam ?? undefined,
      inspectionIds,
    }),
    [companyId, siteIdParam, inspectionIds],
  );

  const [template, setTemplate] = useState<ReportTemplate | null>(null);
  const [nodes, setNodes] = useState<InputWidget[]>([]);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(true);
  // 저작 트리에 변수 동적 옵션 축(optionsFrom) 반영 — 트리 배치본(placed copy)은 배치 시점
  // 스냅샷이라 이후 변수 선언 편집을 모른다. 렌더 진입 전에 변수 선언 기준으로 덮어
  // 변수 선언을 옵션 축의 단일 정본으로 유지한다(개정 4). 트리 없으면 undefined(flat 폴백).
  const effectiveTree = useMemo(
    () =>
      template?.composerWidgetTree?.length
        ? applyVariableDynamicOptions(template.composerWidgetTree, template.variables)
        : undefined,
    [template],
  );
  /**
   * 작성 폼의 **단일 정본 트리** — 렌더(ComposerWidgetTreeRenderer)와 필수 검증
   * (collectMissingRequired)이 같은 값을 읽는다(2026-09-30 S3-b).
   *
   * 저작 트리가 있으면 그것, 없으면 본문 바인딩 파생 `nodes`(= `deriveWriteForm` 산출
   * `InputWidget[]`). `InputWidget`은 `ComposerWidget` union 멤버라 변환층이 없다.
   *
   * ⚠️ 파생 쪽에 `applyVariableDynamicOptions`를 걸지 않는다 — 그 함수는 **배치본(placed copy)의
   * stale 옵션**을 변수 선언 기준으로 정정하는 것이고, 파생은 매번 `template.variables`를 라이브로
   * 읽어 같은 `optionsFrom`·`options`를 이미 싣는다(rc `writeForm.ts:450`, 변수 key가 `seen`으로
   * 선점하므로 같은 키의 본문 바인딩이 별도 노드가 되지도 않는다). 즉 구성상 no-op이고, 걸면
   * 매 렌더 새 배열/객체를 만들어 참조 안정성만 잃는다.
   *
   * ⚠️ 종전 검증 갈래는 `template.composerWidgetTree`(raw)를 읽어 렌더가 보는 `effectiveTree`와
   * 어긋나 있었다 — 이 memo 하나로 그 축도 닫힌다.
   */
  const writeTree = useMemo<ComposerWidget[]>(() => effectiveTree ?? nodes, [effectiveTree, nodes]);

  const [sites, setSites] = useState<Site[]>([]);
  const [siteId, setSiteId] = useState(siteIdParam ?? '');
  // 제목은 사용자 입력이 아니라 제출 시 자동 생성(구 SMS 동형: `${템플릿명} - ${현장명} (${날짜})`)
  // — 기본 정보 카드 제거(2026-07-15 사용자 확정: 진입 시 현장/점검 컨텍스트를 갖고 들어옴).
  const [values, setValues] = useState<Record<string, unknown>>({});

  // (구 개정 4의 평가기준 세트 변수 재resolve 배선(methodReresolve)은 개정 5 선택 전파 모델로
  //  제거 — 세트 select의 writes가 선택 옵션 원천 행의 criteria 합성 객체를 등록형 위젯
  //  bindingKey에 직접 전파한다. finding 점수 재산출은 종전대로 저장 시 서버 compose가 수행.)
  // 초기값(prefill 포함) 확정 신호 — collapseWhenFilled 섹션의 "채워져 있으면 접힘" 1회 판정 시점.
  // prefill이 로딩 게이트 밖(fire-and-forget)이라 마운트 시점 판정으론 빈 값 기준이 되는 것 방지.
  const [initialValuesReady, setInitialValuesReady] = useState(false);
  // 커스텀 모달 위젯(P3) — host가 런타임 resolve한 후보 rows(소스 키별). AI 개선 의견 후보를
  // sortableSelect가 소비할 수 있게 rc 렌더러에 주입한다(모달 스코프는 본문 values 격리라 이 채널 필요).
  const [resolvedRows, setResolvedRows] = useState<Record<string, Record<string, unknown>[]>>({});
  // 후보 resolve 1회 가드(중복 fetch 방지) — 템플릿/컨텍스트 확정 후 최초 1회만 호출.
  const candidatesResolvedRef = useRef(false);
  // 수정 모드 로드 가드 — **어느 문서를 실었나**를 담는다(불리언이 아니다).
  // 형제 칩으로 옮기면 라우트(`/reports/new/composed/:templateKey`)는 그대로고 query만 바뀌어
  // 이 컴포넌트가 언마운트되지 않는다. 불리언 가드였다면 앞 문서를 실은 채 주소만 바뀌어
  // **다른 문서의 내용을 편집하는** 상태가 된다.
  const editLoadedRef = useRef<string | null>(null);
  const editReportRef = useRef<Report | null>(null);
  // 같은 보고서를 **렌더에 쓰는** 사본 — 형제 칩이 조회 기준(점검·양식)으로 삼는다.
  // ref만으로는 로드 완료가 리렌더를 일으키지 않아 칩이 영영 안 뜬다.
  // (이름이 `editReport`가 아닌 이유: 그건 lib/reports에서 import한 **재제출 편집 API**다.)
  const [editTargetReport, setEditTargetReport] = useState<Report | null>(null);

  // 초안 전 단계(CREATING) 이어 작성 — 분할 산물은 CREATING으로 태어나고 작성 완료 저장이
  // 서버 규칙으로 DRAFT 승격한다(2026-09-06 사용자 확정 상태 모델 — 종전 "status===DRAFT면
  // 작성 프레임" 근사를 **대체**한다: 완료 여부가 영속 상태가 되어, 완료한 문서를 다시
  // "이어 작성"으로 권하는 순환이 사라진다). CREATING = "작성" 프레임 + 「작성 완료」 +
  // 이어 작성 제안 / DRAFT = "수정" 프레임 + 「수정 완료」 + 제안 없음.
  // 라벨(헤더 제목·submit 버튼)만 가르고 chrome 구성을 고르는 mode='edit'는 그대로 둔다 —
  // mode는 버튼 세트 자체를 갈라(resolveComposeChromeItems) 라벨 문제에 쓰면 동작이 함께
  // 바뀐다. 로드 전(null)에는 수정으로 두어 비CREATING 문서가 "작성"으로 깜빡이지 않게 한다.
  const isCreatingResume = isEditMode && editTargetReport?.status === 'CREATING';

  // 형제 목록 — 아래 칩이 쓰는 것과 **같은 훅·같은 캐시**라 요청이 늘지 않는다. 페이지가 이걸
  // 더 쓰는 곳은 저장 완료 안내의 잔여 작성 대기 수와 이어 작성 대상 둘이다(2026-09-06 ②③).
  const { siblings } = useSiblingReports(editTargetReport, companyId || undefined);
  // CREATING→DRAFT 승격을 형제 캐시에 즉시 반영하는 데 쓴다(저장 핸들러 — 아래 주석 참조).
  const queryClient = useQueryClient();

  /**
   * 이 문서가 속한 분할 그룹의 표시명(예: '수공구 작업'). 비분할 문서면 빈 문자열이고,
   * 그 경우 제목·완료 안내는 **종전 그대로** 둔다 — 분할이 아닌 문서에 "「」 작성 완료"를
   * 붙이면 없는 범위를 있는 것처럼 말하게 된다.
   *
   * ⚠️ 아래 세 자리(제목·완료 안내·이어 작성)의 문구는 이 라벨을 **인용만** 한다. 분할 축이
   * 무엇인지는 템플릿마다 다르므로(§④ "도메인 지식 0") 문구에 '공종' 같은 이름을 박으면
   * 다른 템플릿이 분할을 쓰는 순간 거짓말이 된다. 발동 축도 문서 데이터뿐이다 —
   * `templateKey`로 분기하지 않는다.
   */
  const splitGroupLabel = editTargetReport?.splitGroupLabel?.trim() || '';

  /** 형제 칩에 쓰는 것과 같은 라벨 파생(그룹 표시명 → 없으면 제목). */
  const siblingLabel = (sibling: Report) => sibling.splitGroupLabel?.trim() || sibling.title;

  /**
   * 제출 후 이어질 흐름(결정 2026-09-06 §1·§3) — web 흐름 레지스트리 **인용**. 편집 모드의
   * `template` state는 report.templateSnapshot으로 세팅되므로(위 applyTemplate) 여기서 읽으면
   * 곧 **스냅샷 판정**이다(서버의 위임 완료 승격 skip과 같은 축 — §6 ⑤). 생성 모드는 live
   * 템플릿인데, 저장 시 바로 그 템플릿이 스냅샷으로 박제되므로 같은 값이다.
   */
  const postSubmitFlow = templatePostSubmitFlow(template);

  /**
   * 검수 순회 맥락(S33/F-3) — 앵커(편집 대상)가 DRAFT+선언+설정 미완이면, 형제 칩의 완료
   * (DRAFT) 건을 상세 대신 편집 URL로 연다(배포 설정 「내용 확인·수정」에서 들어온 검수 세션의
   * 칩 순회). 판정·목적지는 postSubmitFlows 소유 — 여기는 인용만.
   */
  const reviewTraversal = Boolean(
    isEditMode && editTargetReport && isPostSubmitReviewTraversal(editTargetReport),
  );

  /**
   * 분할 카운터가 세는 집합 — 서버 형제 묶음 술어와 같게 **취소 문서를 제외**한다(W3c: 취소가
   * "전원 DRAFT" 게이트를 영구 차단하지 않게 한 그 술어를 화면도 소비한다).
   */
  const countedSiblings = useMemo(
    () => siblings.filter((s) => s.status !== 'CANCELLED'),
    [siblings],
  );

  /**
   * submit 버튼이 놓인 상황(§3) — 라벨 해소는 shared `resolveComposeSubmitLabel` 한 곳이고
   * 여기는 재료만 만든다. 카운터 의미 = "이번 저장으로 완료되는 건수/전체"(완료 술어 = DRAFT —
   * 배포 설정 진입 게이트 "전원 DRAFT"와 같은 축으로 세어야 예고와 판정이 갈리지 않는다).
   * 이 문서 자신은 아직 CREATING이므로 +1이 곧 "이번 저장" 몫이다.
   *
   * mode는 셸에 넘기면 셸 prop이 덮고(무해), 아래 labelOverride 경로에서만 의미를 갖는다 —
   * CREATING 이어 작성은 chrome 구성상 mode='edit'(기존 설계: 버튼 세트를 가르는 축이라 유지)
   * 이지만 라벨 상황은 '작성'(create)이다.
   */
  const submitSituation = useMemo<ComposeSubmitSituation>(
    () => ({
      mode: 'create',
      splitProgress:
        isCreatingResume && countedSiblings.length >= 2
          ? {
              // 완료 = 「작성이 끝난」 형제 = CREATING이 아닌 것(F-57, 2026-09-13). 종전 `=== 'DRAFT'`는
              // 확정·결재까지 간 형제(APPROVED·REJECTED)를 미완으로 세어, 재생성 형제의 버튼이
              // 「(1/2)」로 남고 「→ 배포 설정」이 안 붙었다 — F-51이 게이트·배너에서 좁힌 것과 같은 축.
              completedAfterSave:
                countedSiblings.filter((s) => s.status !== 'CREATING').length + 1,
              total: countedSiblings.length,
            }
          : undefined,
      // 「배포 설정」류 문구는 레지스트리 ctaLabel 인용 — 여기(호스트)와 셸엔 도메인 어휘 0.
      postSubmitCtaLabel: postSubmitFlow?.ctaLabel || undefined,
    }),
    [isCreatingResume, countedSiblings, postSubmitFlow],
  );

  /**
   * 저장 직후 "이어 작성할까요?"로 물을 대상. null이면 모달 없이 종전 동선(상세로).
   * **자동 이동은 하지 않는다**(2026-09-06 사용자 확정 — 저장하자마자 다른 문서가 열리면
   * 납치당한 느낌이 든다). 물어보고 승인했을 때만 옮긴다.
   */
  const [continueTarget, setContinueTarget] = useState<{ href: string; label: string } | null>(
    null,
  );

  // 카탈로그 행 즉시 등록(submitAction 모달 — 결정 2026-07-17 "등록 모달 = 즉시 등록") 호스트 콜백.
  // 성공 시 반환 행을 제안 rows(resolvedRows — resolve 우선 채널이라 여기만 갱신하면 콤보 제안에
  // 즉시 반영)에 합류시키고 rc에 돌려준다(rc가 연결 콤보 텍스트+합성 id 세팅). 실패는 null(rc가
  // 모달 유지 + 인라인 오류). external 공유 페이지는 이 콜백을 배선하지 않아 액션 자체가 숨겨진다.
  const handleCatalogRowSubmit = useCallback(
    async (
      catalogKey: string,
      submitValues: Record<string, string>,
    ): Promise<Record<string, unknown> | null> => {
      if (!siteId) return null;
      try {
        const row = await submitComposerCatalogRow(catalogKey, siteId, submitValues);
        setResolvedRows((prev) => {
          const cur = prev[catalogKey] ?? [];
          // 같은 행(id 일치, id 없는 카탈로그는 name 일치) 갱신이면 교체, 아니면 append.
          const idx = cur.findIndex((r) =>
            row.id !== undefined ? r.id === row.id : r.name === row.name,
          );
          const next =
            idx >= 0 ? cur.map((r, i) => (i === idx ? row : r)) : [...cur, row];
          return { ...prev, [catalogKey]: next };
        });
        // dirty 처리는 별도 불필요 — rc가 이어서 콤보 값을 onChange(handleChange)로 세팅하며 갱신됨.
        return row;
      } catch {
        return null;
      }
    },
    [siteId],
  );
  // photo 위젯 업로드 호스트 배선 — 기존 files/upload(purpose REPORT_ATTACHMENT) 래퍼 재사용,
  // 반환 url을 위젯 값으로 저장(값 갱신은 rc가 onChange=handleChange로 수행 → dirty 자동).
  // 실패는 토스트 + rethrow(rc 위젯이 인라인 오류 표시, 값 불변). companyId 없으면 아래
  // 렌더에서 미배선 degrade(placeholder — suggest 배선과 동일 규약).
  const handlePhotoUpload = useCallback(
    async (file: File): Promise<string> => {
      try {
        const uploaded = await uploadReportFile(file, companyId);
        return uploaded.url;
      } catch (err) {
        showToast(extractApiError(err).message || '사진 업로드에 실패했습니다.', 'error');
        throw err;
      }
    },
    [companyId, showToast],
  );

  // 미저장 이탈 가드(A안): 사용자 입력이 생기면 dirty=true. 뒤로가기 시 확인 모달 + 브라우저 닫기 경고.
  const [dirty, setDirty] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  /**
   * 확인 후 갈 곳. null이면 뒤로가기(`navigate(-1)`)다 — 형제 칩처럼 **목적지가 있는** 이탈은
   * 여기에 담아 두고 승인 시 그리로 간다(뒤로가기로 보내면 엉뚱한 데로 나간다).
   */
  const [pendingLeaveHref, setPendingLeaveHref] = useState<string | null>(null);
  // 되돌리기(reset) 확인 모달. reset은 DEFAULT chrome에서 visibleIn:'edit'라 현재(생성) 화면엔
  // 미노출 — 수정 흐름이 붙으면 노출된다. 동작(초기 스냅샷 복원)은 여기서 완전 배선.
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  // 최초(초기값+prefill 반영) 스냅샷 — 되돌리기 복원 기준. 템플릿 로드 시 1회 기록.
  const initialSnapshotRef = useRef<{
    values: Record<string, unknown>;
    siteId: string;
  } | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  // 작성/미리보기 뷰 전환. preview는 검증 없이 열림. values는 useState라 전환에도 보존(언마운트 금지).
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  /** 미리보기 진입 직전 작성 화면 스크롤 위치 — 돌아올 때 복원용(ComposePreviewView에 전달). */
  const previewFromScrollYRef = useRef(0);

  // 실폼 활성 반응형 뷰(matchMedia) — chrome 항목 뷰별 오버라이드 해소에 사용.
  const activeView = useActiveComposerView();
  // 셸 헤더/하단 바 뷰포트 고정 시 sticky 헤더 top 오프셋(앱 헤더 아래). lg+=0.
  const stickyTop = useStickyChromeTop();

  // 템플릿 로드 + 작성 노드 파생 + 초기값 설정 (생성 전용 — 편집은 아래 편집 로드 effect가 담당)
  useEffect(() => {
    if (!templateKey || isEditMode) return;
    setIsLoadingTemplate(true);
    setInitialValuesReady(false);
    getRenderTemplate(templateKey)
      .then((tpl) => {
        setTemplate(tpl);
        // 작성 폼 골격의 출처는 **본문 바인딩**이다 — `deriveWriteForm`이 그 정본 파생이고,
        // 저작 트리(`composerWidgetTree`)가 있으면 아래 `writeTree` memo가 그것을 우선한다.
        //
        // ⚠️ 2026-09-30(S3-a)에 종전의 `tpl.writeForm?.length ? tpl.writeForm : derive(tpl)` 삼항을
        // 걷었고(값이 있으면 **파생 골격을 버리고** 저장 배열만 쓰는 길이었다), 2026-10-02(S6)에
        // 그 저장 배열 자체(`ReportTemplate.writeForm` — flat override 채널)가 철거됐다.
        // 즉 지금 골격의 출처는 하나뿐이다.
        // (결정 `2026-09-30-writeform-to-composer-widget-tree-consolidation`)
        const formNodes = deriveWriteForm(tpl);
        setNodes(formNodes);
        // 각 노드 defaultValue로 초기화(prefill 값이 없을 때의 fallback)
        const initial: Record<string, unknown> = {};
        for (const node of formNodes) {
          if (node.defaultValue !== undefined) {
            initial[node.bindingKey] = node.defaultValue;
          }
          // N2-B: widget:'table' 노드(=dataDef)는 dataDef.defaultRows의 deep clone으로 seed
          // (prefill/override는 아래에서 덮어씀).
          if (node.widget === 'table') {
            const def = tpl.dataDefs?.find((d) => d.key === node.bindingKey);
            if (def) {
              initial[node.bindingKey] = structuredClone(def.defaultRows);
            }
          }
        }
        // 트리(composerWidgetTree)에 저작된 defaultValue를 파생분 위에 덮는다 — 같은 bindingKey면
        // 트리가 정본이다(저작 트리가 최신 의도를 담는다).
        // group/모달 제외 규약/formTable 고정 셀 위젯 범위는 헬퍼 doc 참조.
        Object.assign(
          initial,
          collectWidgetDefaultValues(tpl.composerWidgetTree ?? [], tpl.dataDefs),
        );
        setValues(initial);
        // 되돌리기 기준 스냅샷 — prefill 컨텍스트 없으면 여기서 확정, 있으면 아래 resolve 후 갱신.
        initialSnapshotRef.current = { values: initial, siteId };

        // N1: 편집 가능한 prefill — 컨텍스트(현장/점검/조직)가 있으면 input 바인딩의
        // prefillFrom을 서버 resolve해 초기값을 seed(사용자가 수정 가능). 실패 시 graceful(무시).
        if (companyId || siteIdParam || (inspectionIds && inspectionIds.length > 0)) {
          getComposerPrefill(templateKey, suggestContext)
            .then((prefill) => {
              if (Object.keys(prefill).length === 0) return;
              // '내 입력 누적' 사전 키(suggestion-dict:*)는 values 병합에서 제외 — values에 실리면
              // 제출 content에 사전 행 배열이 통째로 박제된다(사전은 ComposerDataStore가 정본,
              // 서버도 저장 시 strip). 제안 도달은 아래 resolvedRows 채널이 담당.
              const prefillValues: Record<string, unknown> = {};
              for (const [key, value] of Object.entries(prefill)) {
                if (!isSuggestionDictKey(key)) prefillValues[key] = value;
              }
              // prefill을 defaultValue 위에 덮어쓴다(prefill 우선, 편집 가능).
              setValues((prev) => ({ ...prev, ...prefillValues }));
              // 카탈로그 list 후보 소스(서버 resolvePrefill이 result[sourceKey]=rows로 넣는 값)는 values
              // 채널만으론 **모달 안** sortableSelect에 도달하지 못한다(ModalTriggerButton이 children에
              // 빈 modalValues를 넘겨 본문 values와 격리 — resolvedRows만 전파). 소스 키·변수
              // optionsFrom 키·사전 키의 배열을 resolvedRows에 합류시킨다(extractListRowsFromPrefill —
              // 사전 키는 rc resolveSortableRows ①(resolvedRows) 우선이라 본문·모달 안 콤보 모두 도달).
              // AI 후보(별도 effect)와는 키가 달라 충돌 없음 — 이미 주입된 rows(AI)가 정본이 되도록
              // prev를 뒤에 병합.
              const listRows = extractListRowsFromPrefill(prefill, tpl, { includeDict: true });
              if (Object.keys(listRows).length > 0) {
                setResolvedRows((prev) => ({ ...listRows, ...prev }));
              }
              // 스냅샷도 prefill 반영본으로 갱신(되돌리기 = prefill 포함 초기 상태 — 사전 키 제외).
              initialSnapshotRef.current = {
                values: { ...initial, ...prefillValues },
                siteId,
              };
            })
            .catch(() => {
              // prefill 실패(네트워크/없음)는 무시 — defaultValue fallback으로 동작.
            })
            // 성공/실패 무관 초기값 확정 — collapseWhenFilled 섹션의 접힘 판정 시점 신호.
            .finally(() => setInitialValuesReady(true));
        } else {
          // prefill 컨텍스트 없음 — defaultValue만으로 초기값 확정.
          setInitialValuesReady(true);
        }
      })
      .catch((err) => {
        showToast(extractApiError(err).message || '템플릿을 불러오지 못했습니다.', 'error');
        navigate(-1);
      })
      .finally(() => setIsLoadingTemplate(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateKey]);

  // 편집 대상이 바뀔 때(형제 칩 이동) 문서에 딸린 상태 비우기.
  //
  // 형제 이동은 라우트(`/reports/new/composed/:templateKey`)가 그대로고 query만 바뀌어
  // **언마운트가 없다.** 폼 값·템플릿·스냅샷은 아래 로드가 통째로 덮지만 이 셋은 덮지 않아
  // 앞 문서의 흔적이 남는다: dirty(앞 문서에서 친 글자 때문에 새 문서가 이탈 경고를 띄운다),
  // resolvedRows(앞 문서의 제안 후보), mode(미리보기에서 옮겨왔다면 미리보기인 채).
  useEffect(() => {
    if (!isEditMode) return;
    setDirty(false);
    setResolvedRows({});
    setMode('edit');
  }, [editReportId, isEditMode]);

  // 수정(edit) 데이터 로드 — 편집 대상 보고서를 불러와 폼 구조/값을 seed(구 SMS ?editReportId= 동형).
  // 폼 구조는 report.templateSnapshot(생성 시 박제) 우선, 없으면 getRenderTemplate(templateKey) 폴백 —
  // content가 snapshot 기준으로 저작되고 상세도 snapshot을 렌더하므로 키 일치·WYSIWYG 보장(결정 B).
  useEffect(() => {
    if (!isEditMode || !companyId || editLoadedRef.current === editReportId) return;
    let cancelled = false;
    setIsLoadingTemplate(true);
    setInitialValuesReady(false);
    canEditReport(editReportId, companyId)
      .then((perm) => {
        if (cancelled) return;
        if (perm.canEdit === false) {
          navigate(`/reports/${editReportId}`, { replace: true });
          return;
        }
        return getReport(editReportId, companyId).then((report) => {
          if (cancelled) return;
          editLoadedRef.current = editReportId;
          editReportRef.current = report;
          setEditTargetReport(report);
          // 폼 구조: 박제 스냅샷 우선, 없으면 templateKey로 live 로드.
          const applyTemplate = (tpl: ReportTemplate) => {
            if (cancelled) return;
            setTemplate(tpl);
            // 생성 경로(위 effect)와 같은 규약 — 파생이 override를 품는다(rc `writeForm.ts:422`).
            const formNodes = deriveWriteForm(tpl);
            setNodes(formNodes);
            const seededValues = (report.content ?? {}) as Record<string, unknown>;
            setValues(seededValues);
            setSiteId(report.siteId ?? '');
            initialSnapshotRef.current = {
              values: seededValues,
              siteId: report.siteId ?? '',
            };
            setInitialValuesReady(true);
            setIsLoadingTemplate(false);
            // 편집 모드 카탈로그 후보 rows — 신규 모드는 prefill 채널(위 effect)로 주입되지만
            // 편집 로드는 content만 seed해 콤보 제안이 전부 degrade됐다(2026-08-24 DLR 확인자/장소
            // 실측 적발 — SMS 콤보 시절부터 잠복). prefill의 "값" 채널은 쓰지 않는다(content가
            // 정본 — setValues 금지), list rows·제안 사전만 추출해 resolvedRows로 주입.
            const rowsTemplateKey = report.templateKey ?? tpl.key;
            if (rowsTemplateKey) {
              // 문서가 조립된 위험도 평가 방식 — 안 실으면 서버가 **현장 세트**로 resolve해 그
              // 기준표 rows가 resolvedRows에 앉고(rc는 resolvedRows > content), 3단계 문서의
              // 「위험성 수준」 select가 현장 3x3 기준으로 옵션 0·현재 값 미표시가 된다
              // (2026-09-14 위험성평가 결과서 실측). 출처 순서는 composedStoredRiskMethod가 소유.
              const storedMethod = resolveStoredFindingRiskMethod(tpl, seededValues);
              // 컨텍스트는 문서에서(editReportPrefillContext — 현장·공종·조직). 공종(분할 그룹)
              // 축은 서버가 그 라벨로 「그 공종에서 마지막에 쓴 장소」를 후보 맨 위로 올린다
              // (결정 2026-09-08). 값 채널은 여전히 안 쓴다(위 주석 — content가 정본): **후보
              // 순서만** 바뀌고 시작값은 채워지지 않는다.
              getComposerPrefill(rowsTemplateKey, {
                ...editReportPrefillContext(report, companyId),
                // 박제된 방식이 없는 문서(위험도 축 없음·구형)는 키 자체를 싣지 않는다(종전 동일).
                ...(storedMethod !== undefined ? { findingRiskMethod: storedMethod } : {}),
              })
                .then((prefill) => {
                  if (cancelled) return;
                  const listRows = extractListRowsFromPrefill(prefill, tpl, {
                    includeDict: true,
                  });
                  if (Object.keys(listRows).length > 0) {
                    // 키 단위 안전 병합 — 방향: **이미 실질 rows가 주입된 키**(AI 후보·등록 즉시
                    // append)는 보존(종전 `...prev` 우선 의도), 미주입·빈 배열 키만 서버 rows로
                    // 채운다. 통째 `{...listRows, ...prev}`는 prev에 빈 배열이 앉아 있으면 서버
                    // rows가 그 빈 값에 가려지는 함정(주입 경합 시 제안이 영영 빈 목록).
                    setResolvedRows((prev) => {
                      const next = { ...prev };
                      for (const [key, rows] of Object.entries(listRows)) {
                        const existing = next[key];
                        if (!Array.isArray(existing) || existing.length === 0) next[key] = rows;
                      }
                      return next;
                    });
                  }
                })
                .catch(() => {
                  // rows 없이도 편집 가능(기존 degrade 동작) — best-effort.
                });
            }
          };
          if (report.templateSnapshot) {
            applyTemplate(report.templateSnapshot);
          } else if (report.templateKey) {
            return getRenderTemplate(report.templateKey).then(applyTemplate);
          } else {
            // 스냅샷/키 둘 다 없으면 편집 불가 — 상세로 회귀.
            navigate(`/reports/${editReportId}`, { replace: true });
          }
        });
      })
      .catch((err) => {
        if (cancelled) return;
        showToast(extractApiError(err).message || '보고서를 불러오지 못했습니다.', 'error');
        navigate(`/reports/${editReportId}`, { replace: true });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, companyId, editReportId]);

  // 현장 목록 로드
  useEffect(() => {
    if (!companyId) return;
    sitesApi
      .findAll(companyId)
      .then((data) => setSites(data))
      .catch(() => {});
  }, [companyId]);

  // 커스텀 모달 위젯(P3) — 템플릿에 AI 개선 의견 후보를 소비하는 sortableSelect가 있으면, 점검 컨텍스트로
  // 후보를 resolve해 resolvedRows로 주입한다. 소스 키는 파트별(COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS:
  // guidance/summary/conclusion)만 감지 — 감지된 파트만 서버에 요청(parts)해 캐시 빈 상태의 전체 AI 생성
  // 트리거를 줄인다(결정 2026-07-16, 합본 키는 파트 3키 조합으로 대체돼 제거).
  // 구 SMS 진입 플로우(ReportTypeSelectModal → sessionStorage)의 카탈로그 resolve 대체.
  // 점검 컨텍스트가 없으면 호출 생략 = 후보 없음 = 버튼 숨김(hideWhenNoData, 구 SMS 동형).
  useEffect(() => {
    if (isEditMode) return; // 편집은 저장된 content로 seed — 새 AI 후보 생성 안 함.
    if (candidatesResolvedRef.current || !template) return;
    const tree = template.composerWidgetTree;
    if (!tree?.length) return;
    const sourceKeys = collectSortableSelectOptionSources(tree);
    const detectedParts = (
      Object.keys(COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS) as ImprovementCandidatePart[]
    ).filter((p) => sourceKeys.includes(COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS[p]));
    if (detectedParts.length === 0) return;
    // 점검 컨텍스트 없음 → 후보 없음(버튼 숨김). 위임 작성(external/composed)은 인증 API 불가라 여기 미도달.
    if (!companyId || !inspectionIds?.length) return;
    candidatesResolvedRef.current = true;
    // 진입 전 사용자 입력(타입 선택 모달 → sessionStorage)을 1회 소비(읽고 즉시 제거)해 첫 생성에 동봉.
    // 서버 동일성 비교가 재생성 여부를 결정(같은 입력이면 캐시 재사용). 없으면 undefined(무지시).
    let entryGuidance: string | undefined;
    if (templateKey) {
      const stored = sessionStorage.getItem(composedEntryGuidanceKey(templateKey));
      if (stored) {
        entryGuidance = stored;
        sessionStorage.removeItem(composedEntryGuidanceKey(templateKey));
      }
    }
    generateImprovementCandidates(inspectionIds[0], companyId, entryGuidance, detectedParts)
      .then((result) => {
        setResolvedRows((prev) => {
          const next = { ...prev };
          for (const p of detectedParts) {
            const rows = improvementCandidatesToRows(result, [p]); // 그 파트 rows만
            if (rows.length > 0) next[COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS[p]] = rows;
          }
          return next;
        });
      })
      .catch(() => {
        // 후보 생성 실패(네트워크/없음)는 무시 — 후보 미주입 = 버튼 숨김(구 SMS 동형).
      });
  }, [template, companyId, inspectionIds, templateKey, isEditMode]);

  // 평가방식(findingRiskMethod) 변경 재resolve(P4e) — **카탈로그 파라미터 지정이 참조하는 변수**의
  // 값이 바뀌면 그 방식으로 서버 prefill을 재조회해 위험도 파생 필드·기준표·
  // __seed만 교체(비위험도 편집 보존). 판정은 rc 단일 지점(collectCatalogParamVariableKeys — 서버
  // resolveFindingRiskMethod의 거울)이 소유한다.
  // 옛 방식 위험도 편집은 폐기(새 어휘에서 무의미). 결정 2026-07-20-risk-estimation-snapshot-user-edit-priority.
  //
  // **편집 모드도 같다**(결정 2026-09-14 RA 전환 문서 「편집 모드 평가 방식 변경」 개정 — 종전엔
  // `if (isEditMode) return`으로 막았다). 07-20의 의미는 「같은 방식에서의 재prefill이 편집을 덮지
  // 않는다」이고, 방식이 바뀌면 옛 어휘의 위험도는 폐기가 규약이다 — 신규 작성이 이미 그렇게 동작한다.
  // 편집이 신규와 다른 두 가지: ① 컨텍스트는 query가 아니라 **문서**에서(점검 = junction, 현장 =
  // report.siteId, 공종 = splitGroupLabel — 편집 로드 prefill과 같은 재료 + 점검) ② 병합은 **현재 행
  // 기준**(`rowBasis:'current'` — 저장 content가 행의 정본이라 지운 행이 되살아나거나 추가 행이
  // 사라지면 안 된다). baseline은 편집 로드가 `initialValuesReady`를 올리는 순간의 시그니처라
  // 로드 직후 저장값으로 재조회가 돌지 않는다. 서버 update()가 박제·방식 채널을 같은 축으로 갱신한다.
  const hasMethodTrigger = useMemo(
    () => collectCatalogParamVariableKeys(template).size > 0,
    [template],
  );
  // 트리거 변수들의 현재 값 시그니처(단일 스칼라) — 트리거가 여럿일 수 있어 값 하나 비교로는 부족하다.
  const methodTriggerSig = useMemo(
    () => methodTriggerSignature(template, values),
    [template, values],
  );
  // 재resolve가 마지막으로 반영한 시그니처 — 최초 prefill 확정 시점의 값을 baseline으로 잡아
  // mount/초기 prefill엔 재조회하지 않고, 사용자의 실제 방식 변경만 트리거로 삼는다.
  const reresolvedMethodRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (initialValuesReady) reresolvedMethodRef.current = methodTriggerSig;
    // methodTriggerSig는 baseline 확정 순간의 값만 필요 — 이후 변경 감지는 아래 effect가 담당.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValuesReady]);
  useEffect(() => {
    if (!initialValuesReady || !template || !templateKey || !hasMethodTrigger) return;
    // 비동기 클로저에서 null 좁힘이 유지되지 않으므로 non-null 캡처.
    const tpl = template;
    // 편집/신규가 갈리는 세 축(컨텍스트·템플릿 키·병합 기준)과 게이트(편집 모드에서 문서를 아직 안
    // 쥐었거나 다른 문서 / prefill과 같은 컨텍스트 게이트)는 순수 판정 resolveMethodReresolveRequest가
    // 소유한다(composedEditContext — co-located spec).
    const request = resolveMethodReresolveRequest({
      isEditMode,
      editReportId,
      loadedEditReport: editReportRef.current,
      companyId,
      siteIdParam,
      inspectionIds,
      suggestContext,
      routeTemplateKey: templateKey,
    });
    if (!request) return;
    if (methodTriggerSig === reresolvedMethodRef.current) return; // 변경 없음/초기값 — skip
    // 서버에 실어 보낼 방식 값 — 지정(literal/variable)을 서버와 같은 규칙으로 해소.
    const methodStr = resolveFindingRiskMethodValue(tpl, values);
    let cancelled = false;
    const timer = setTimeout(() => {
      getComposerPrefill(request.templateKey, {
        ...request.context,
        findingRiskMethod: methodStr,
      })
        .then((fresh) => {
          if (cancelled || Object.keys(fresh).length === 0) return;
          reresolvedMethodRef.current = methodTriggerSig; // 반영 기준 갱신(중복 재조회 방지)
          setValues((prev) => mergeMethodReresolve(prev, fresh, tpl, request.mergeOptions));
          // 기준표/변수 옵션 소스(risk-assessment-configs 등) resolvedRows도 갱신 — 초기 prefill의
          // listRows 주입과 동형(모달 스코프 도달 채널, 사전 키 제외). config 목록이 방식에 무관해도 무해.
          const listRows = extractListRowsFromPrefill(fresh, tpl);
          if (Object.keys(listRows).length > 0) {
            setResolvedRows((prev) => ({ ...prev, ...listRows }));
          }
        })
        .catch(() => {
          // 재조회 실패(네트워크/컨텍스트)는 비차단 — 기존 값 유지.
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // 트리거 시그니처 변경만 트리거(다른 값은 무관). suggestContext는 안정 참조(useMemo).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methodTriggerSig, initialValuesReady, templateKey, hasMethodTrigger]);

  // 사진 AI 분석(템플릿 photoAnalysis 선언 소비 — 범용 훅, 도메인 키는 선언 소유).
  // handleChange 전이 감지로 발화하므로 서버 시드(setValues 직접 경로)에는 반응하지 않는다.
  const photoAnalysis = useComposerPhotoAnalysis({
    template,
    values,
    setValues,
    companyId,
    showToast,
  });

  const handleChange = (bindingKey: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [bindingKey]: value }));
    setDirty(true);
    // 전이 감지는 **호스트 머지 뒤에** — 훅의 행 상태 쓰기(분석 중 표시)도 함수형 갱신이라,
    // 앞에 두면 이 머지(키 통째 교체)가 그 쓰기를 큐 순서로 덮어 지운다. 감지 기준(prev)은
    // 갱신 전 스냅샷인 현재 렌더의 values를 그대로 쓴다.
    photoAnalysis.onFieldChange(bindingKey, value, values);
  };

  const handleSubmit = async () => {
    if (!templateKey || !companyId) return;
    if (!siteId) {
      showToast('현장을 선택해주세요.', 'error');
      return;
    }
    // 필수(required) 미입력 제출 차단 — 구 SMS "…를 선택해주세요"의 데이터 기반 일반화.
    // 렌더와 **같은 트리**(writeTree)를 본다 — 저작 트리든 본문 바인딩 파생이든 검증 규칙은 하나다
    // (현재 뷰 숨김·visibleWhen/enabledWhen 미충족 제외. 파생 노드는 그 축을 갖지 않아 무영향).
    // ⚠️ 파생 경로에서 이 호출은 **언제나 `[]`**라 인자를 `effectiveTree ?? []`로 되돌려도 화면이
    // 똑같다 — 이 축의 탐지기는 `__tests__/newComposedSiblingChips.test.tsx`의 **인자 단언**뿐이다.
    const missing = collectMissingRequired(writeTree, values, activeView);
    if (missing.length > 0) {
      // 안내 문구·스크롤/포커스는 rc 공용(missingRequiredToastMessage/focusMissingRequiredField)
      // — 토스트 호출만 host 소유. 반복 행 안 위젯은 rc 헬퍼가 접두 매칭으로 첫 행 인스턴스를 잡는다.
      showToast(missingRequiredToastMessage(missing), 'error');
      focusMissingRequiredField(missing[0].id);
      return;
    }
    setIsSubmitting(true);
    // 분석 진행 상태(UI 전용 행 필드)는 저장 payload에서 제거 — 분석 중 저장에도 content 비잔존
    // (photoAnalysis 선언 없는 템플릿은 원본 참조 그대로라 종전 경로 무변).
    const contentValues = photoAnalysis.sanitizeContent(values);
    try {
      // 수정 모드 — content만 저장(제목 재생성 안 함). REJECTED=editReport(재제출 편집), 그 외(DRAFT)=updateReport.
      if (isEditMode) {
        const status = editReportRef.current?.status;
        if (status === 'REJECTED') {
          await editReport(editReportId, companyId, { content: contentValues as unknown as ReportContent });
        } else {
          await updateReport(editReportId, { content: contentValues as unknown as ReportContent }, companyId);
        }
        setDirty(false);

        // CREATING 문서의 content 저장은 서버가 DRAFT로 승격했다(reports.service.update의
        // 전이 규칙). 로컬에 CREATING이 낡은 채 남으면 두 자리가 거짓이 된다: ① 이 문서를
        // 형제 이동으로 다시 열 때 "작성" 프레임으로 보인다 ② 형제 목록 캐시(useSiblingReports,
        // staleTime 5분)가 이 문서를 여전히 "이어 쓸 문서"로 세어, 다음 문서의 저장 안내가
        // 방금 완료한 문서를 다시 권한다 — 이 모델이 없애려는 바로 그 순환이다.
        if (isCreatingResume) {
          const promoted = editReportRef.current
            ? { ...editReportRef.current, status: 'DRAFT' as Report['status'] }
            : null;
          editReportRef.current = promoted;
          setEditTargetReport(promoted);
          queryClient.setQueriesData<Report[]>(
            { queryKey: ['siblingReports'] },
            (cached) =>
              cached?.map((s) => (s.id === editReportId ? { ...s, status: 'DRAFT' } : s)),
          );
          // 배포 설정 화면의 캐시도 이 승격에 걸린다 — 그 응답은 문서 하나가 아니라 **묶음
          // 전체**(형제 목록 + 게이트 판정)라, 방금 승격한 한 건 때문에 묶음 **전 건**의 캐시가
          // 함께 낡는다. 그래서 키를 앵커로 좁히지 않고 prefix 전체를 무효화한다. 바로 아래
          // 직행(§3)이나 상세 진입 라우팅(§5)으로 그 화면에 곧장 들어가므로, 이게 없으면 첫
          // 페인트가 "아직 작성 중"인 낡은 목록·게이트가 된다.
          queryClient.invalidateQueries({ queryKey: ['tbmDistributionSetup'] });
        }

        // 마지막 건 직행(2026-09-06 §3 — 사용자 확정: 저장+승격+흐름 진입 한 번에). 술어는
        // 버튼이 「→ {ctaLabel}」로 예고한 그것 그대로다: **이번 저장으로 형제 전원이 DRAFT**
        // ∧ 스냅샷 선언. 버튼이 전환을 예고했으므로 완료 토스트·이어 작성 모달은 두지 않는다
        // (이중 안내 금지). 위임/타인 CREATING이 남아 예고가 (k/N)이었다면 직행하지 않는다 —
        // 그 경우의 안내는 상세 진입 라우팅(§5, resolvePostSubmitEntryRoute)이 맡는다.
        if (
          isCreatingResume &&
          postSubmitFlow &&
          // 「전원 작성 끝」 = CREATING이 하나도 없다(F-57 — 위 카운터·F-51 게이트와 같은 축).
          countedSiblings.every((s) => s.id === editReportId || s.status !== 'CREATING')
        ) {
          navigate(postSubmitFlow.setupHref(editReportId), {
            state: {
              from: entryStateRef.current?.from,
              fromLabel: entryStateRef.current?.fromLabel,
            },
          });
          return;
        }

        // 저장한 것이 **무엇이었는지**와 **무엇이 남았는지**를 같이 알린다(2026-09-06 ②).
        // 분할 문서는 화면 하나가 그룹 하나라, "저장됐다"만으로는 방금 무엇을 끝냈는지도
        // 몇 건이 더 남았는지도 알 길이 없다. 잔여·다음 대상은 칩과 같은 목록에서 세고
        // 편집 가능 판정도 칩 목적지와 같은 술어(작성 대기 CREATING)를 쓴다 — 안내와 링크가
        // 갈리지 않게. 방금 저장한 문서는 위에서 DRAFT로 반영돼 자연히 잔여에서 빠진다.
        const writableSiblings = siblings.filter(
          (s) => s.id !== editReportId && isSiblingAwaitingWrite(s, user?.id),
        );
        if (splitGroupLabel) {
          const verb = isCreatingResume ? '작성' : '수정';
          showToast(
            writableSiblings.length > 0
              ? `「${splitGroupLabel}」 ${verb} 완료. 작성할 문서 ${writableSiblings.length}건(${writableSiblings
                  .map(siblingLabel)
                  .join('·')})이 남았습니다.`
              : `「${splitGroupLabel}」 ${verb} 완료.`,
            'success',
          );
        }

        // 이어 작성 제안(2026-09-06 ③) — 초안 전 단계(CREATING)를 쓰던 흐름에서만. DRAFT
        // 수정·반려 재제출(수정 프레임)은 그 문서를 고치러 들어온 것이라 다른 문서로 유도할
        // 자리가 아니다. 다음 = 칩 순서상 현재 문서 **다음**의 작성 대기 문서(끝이면 앞으로
        // 돌아 첫 건).
        const currentIndex = siblings.findIndex((s) => s.id === editReportId);
        const rotated =
          currentIndex >= 0
            ? [...siblings.slice(currentIndex + 1), ...siblings.slice(0, currentIndex)]
            : siblings;
        const nextSibling = rotated.find(
          (s) => s.id !== editReportId && isSiblingAwaitingWrite(s, user?.id),
        );
        if (isCreatingResume && nextSibling) {
          setContinueTarget({
            href: resolveSiblingWriteHref(nextSibling, user?.id),
            label: siblingLabel(nextSibling),
          });
          return; // 상세 이동은 보류 — 모달의 두 갈래가 각각 옮긴다
        }

        navigate(`/reports/${editReportId}`, {
          state: {
            from: entryStateRef.current?.from,
            fromLabel: entryStateRef.current?.fromLabel,
          },
        });
        return; // finally에서 setIsSubmitting(false)
      }
      // 제목 자동 생성 — 구 SMS 동형(`안전관리상태보고서 - ${siteName} (${today})`).
      // titleTemplate 지정 템플릿은 서버 치환 제목이 이 값보다 우선한다(2026-08-17 결정) —
      // 여기 하드코딩은 미지정 템플릿 하위호환용.
      const siteName = sites.find((s) => s.id === siteId)?.name || '';
      const today = new Date().toLocaleDateString('ko-KR');
      const report = await createComposedReport({
        companyId,
        siteId,
        templateKey,
        title: `${template?.name || '보고서'} - ${siteName} (${today})`,
        content: contentValues,
        inspectionIds,
      });
      setDirty(false); // 저장 완료 — 이탈 가드 해제(이후 이동/닫기에 경고 안 뜸)
      // 단건(비분할) + 선언(S8) — 버튼이 「→ {ctaLabel}」로 예고했으므로 상세 대신 흐름 직행.
      // 생성 문서는 DRAFT+미설정으로 태어나(composed 생성 경로는 tbmSignatureMode 미설정)
      // 상세로 보내도 §5 라우팅이 흐름으로 되보내니, 처음부터 직행이 왕복을 줄인다.
      if (postSubmitFlow) {
        navigate(postSubmitFlow.setupHref(report.id), {
          state: {
            from: entryStateRef.current?.from,
            fromLabel: entryStateRef.current?.fromLabel,
          },
        });
        return;
      }
      // 진입점(점검 상세 등)을 상세 back-link으로 전파 — 없으면 상세가 기본 '목록'으로 폴백.
      navigate(`/reports/${report.id}`, {
        state: {
          from: entryStateRef.current?.from,
          fromLabel: entryStateRef.current?.fromLabel,
        },
      });
    } catch (err) {
      showToast(extractApiError(err).message || '보고서 작성에 실패했습니다.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 되돌리기 — 입력값을 최초 스냅샷(초기값+prefill)으로 복원. 확인 모달 승인 시 실행.
  const handleReset = () => {
    const snap = initialSnapshotRef.current;
    if (snap) {
      setValues(snap.values);
      setSiteId(snap.siteId);
      setDirty(false);
    }
    setResetConfirmOpen(false);
  };

  // 뒤로가기 — 작성 중(dirty)이면 확인 모달, 아니면 즉시 이동.
  // (앱 내 메뉴 클릭 이탈은 <BrowserRouter>라 useBlocker 불가 → data router 전환 work-stream에서.)
  const handleBack = () => {
    if (dirty) {
      setPendingLeaveHref(null); // 뒤로가기 = 목적지 없음
      setLeaveConfirmOpen(true);
    } else navigate(-1);
  };

  /**
   * 형제 칩 이동 가로채기(`SiblingReportChips.interceptNavigate` 계약) — 미저장 변경이 있으면
   * 링크 기본 이동을 막고 확인 모달로 돌린다. 저장하지 않은 채 다음 문서로 넘어가면 그
   * 문서의 입력이 조용히 사라진다(뒤로가기 가드와 같은 이유, 같은 모달).
   */
  const handleSiblingNavigate = (href: string): boolean => {
    if (!dirty) return false; // 가로채지 않음 — Link가 그대로 이동
    setPendingLeaveHref(href);
    setLeaveConfirmOpen(true);
    return true;
  };

  /** 이탈 확인 승인 — 목적지가 있으면 그리로, 없으면 뒤로가기. */
  const confirmLeave = () => {
    setLeaveConfirmOpen(false);
    setDirty(false);
    const href = pendingLeaveHref;
    setPendingLeaveHref(null);
    if (href) {
      navigate(href, {
        state: {
          from: entryStateRef.current?.from,
          fromLabel: entryStateRef.current?.fromLabel,
        },
      });
    } else {
      navigate(-1);
    }
  };

  /**
   * 이어 작성 승인 — 다음 형제의 **편집 화면**으로. 라우트가 같아 query만 갈리고, 편집 로드
   * effect(문서 id 가드)가 폼을 그 문서로 갈아끼운다.
   */
  const confirmContinueNext = () => {
    const href = continueTarget?.href;
    setContinueTarget(null);
    if (!href) return;
    navigate(href, {
      state: {
        from: entryStateRef.current?.from,
        fromLabel: entryStateRef.current?.fromLabel,
      },
    });
  };

  /** 이어 작성 거절 — 종전 동선 그대로 방금 저장한 문서의 상세로. */
  const declineContinueNext = () => {
    setContinueTarget(null);
    navigate(`/reports/${editReportId}`, {
      state: {
        from: entryStateRef.current?.from,
        fromLabel: entryStateRef.current?.fromLabel,
      },
    });
  };

  // 브라우저 레벨 이탈(탭 닫기·새로고침·주소창) — 네이티브 경고창(커스텀 모달 불가, 브라우저 정책).
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // chrome 버튼 동작 매핑(비주얼은 ComposeWritePageShell이 담당). actionId(내장)/link(커스텀).
  const getButtonProps = (
    item: ComposeChromeItem,
    _slot: ComposeChromeSlot,
  ): ComposeChromeButtonProps => {
    if (item.actionId === 'submit') {
      return {
        onClick: handleSubmit,
        disabled: isSubmitting,
        // CREATING 이어 작성: 셸 mode는 'edit'(chrome 구성 축 — 위 isCreatingResume doc 참조)
        // 이고 셸은 situation.mode를 자기 mode prop으로 덮으므로, '작성' 상황 라벨(카운터·
        // 전환 접미 포함)은 여기서 **같은 shared 해소 함수**를 create 상황으로 호출해 넘긴다 —
        // 해소 로직은 여전히 한 곳이다(문구 복제 없음). item.label은 실제 셸과 같게 기본
        // 라벨 대체로만 전달한다(§3 — 접미는 파생 층 소유).
        labelOverride: isSubmitting
          ? (isEditMode && !isCreatingResume ? '수정 중...' : '작성 중...')
          : isCreatingResume
            ? resolveComposeSubmitLabel(submitSituation, item.label)
            : undefined,
      };
    }
    if (item.actionId === 'preview') {
      return {
        // 스크롤 위치는 **여기서** 잡아야 한다 — 미리보기가 마운트된 뒤엔 작성 화면이 사라져
        // 문서가 짧아지고 브라우저가 스크롤을 잘라둔 값만 남는다.
        onClick: () => {
          previewFromScrollYRef.current = window.scrollY;
          setMode('preview');
        },
        disabled: !template,
      };
    }
    if (item.actionId === 'reset') {
      return { onClick: () => setResetConfirmOpen(true) };
    }
    if (item.actionId === 'cancel') {
      return { onClick: () => navigate('/reports') };
    }
    if (item.link) {
      const href = item.link.href;
      return {
        onClick: () => {
          if (href.startsWith('http')) window.open(href, '_blank', 'noopener');
          else navigate(href);
        },
      };
    }
    return { onClick: () => {} };
  };

  /**
   * 셸 헤더 제목. 분할 문서는 이 화면이 그룹 **하나**를 쓰는 자리라, 양식명만 말하면 지금
   * 어느 그룹을 쓰고 있는지 화면 어디에도 없다(칩은 이동 수단이지 현재 위치 표시가 아니다).
   */
  const shellTitle = `${(template?.name || '보고서').trim()} ${
    isEditMode && !isCreatingResume ? '수정' : '작성'
  }${splitGroupLabel ? ` — ${splitGroupLabel}` : ''}`;

  // 미리보기 뷰 — 전체화면(작성 폼/기본정보 숨김). values는 useState라 보존.
  if (mode === 'preview' && template) {
    return (
      <ComposePreviewView
        template={template}
        values={values}
        onBack={() => setMode('edit')}
        restoreScrollY={previewFromScrollYRef.current}
      />
    );
  }

  return (
    <div className="min-h-full">
      {/* 실제 작성 화면 = admin 작성폼 미리보기와 동일한 ComposeWritePageShell 공유(비주얼 parity). */}
      <ComposeWritePageShell
        config={template?.composeChrome}
        activeView={activeView}
        mode={isEditMode ? 'edit' : 'create'}
        // 생성 모드의 상황(단건+선언 CTA) 해소는 셸이 담당 — mode는 셸 prop이 정본이라
        // 편집 모드에선 이 값이 있어도 base 라벨로 수렴한다(무해).
        submitSituation={submitSituation}
        title={shellTitle}
        subtitle={sites.find((s) => s.id === siteId)?.name}
        getButtonProps={getButtonProps}
        onBack={handleBack}
        backLabel={backLabel}
        stickyChrome
        stickyTopOffset={stickyTop}
        className="min-h-full w-full"
      >
        {isLoadingTemplate ? (
          <p className="text-sm text-ink-400">템플릿 로딩 중...</p>
        ) : (
          // 섹션 간 간격 24px — 구 작성 페이지(space-y-6)와 동일(2026-08-04 여백 정렬).
          <div className="space-y-6">
            {/* 형제 그룹 칩(결정 2026-09-01 §④의 작성 화면 확장, 2026-09-06) — 분할로 함께 태어난
                문서 사이를 **작성 중에** 오간다(저장 → 다음 그룹). 상세와 같은 자리(콘텐츠 최상단)에
                두고, 셸 헤더 바는 admin 작성폼 미리보기와 공유하는 WYSIWYG 구조라 건드리지 않는다.
                작성이 남은 형제는 편집 URL로, 검수 순회 맥락(S33/F-3)에서는 완료(DRAFT) 형제도
                편집 URL로, 그 외는 상세로 열린다(resolveWriteScreenSiblingHref).
                형제가 1건이거나 점검에 안 걸린 문서면 컴포넌트가 스스로 null(노출은 파생). */}
            {isEditMode && editTargetReport && (
              <SiblingReportChips
                report={editTargetReport}
                companyId={companyId || undefined}
                entryState={entryStateRef.current}
                resolveHref={(sibling) =>
                  resolveWriteScreenSiblingHref(sibling, user?.id, reviewTraversal)
                }
                interceptNavigate={handleSiblingNavigate}
              />
            )}
            {/* 기본 정보 카드 제거(2026-07-15 사용자 확정) — 정상 진입은 현장/점검 컨텍스트를 쿼리로
                갖고 들어오고(현장명=chrome 헤더 subtitle 표시), 제목은 제출 시 자동 생성(구 SMS 동형).
                점검 안내 배지만 유지. 현장 선택은 컨텍스트 없이 진입한 경우(직접 URL 등)의 fallback. */}
            {inspectionIds && inspectionIds.length > 0 && (
              <p className="text-xs text-brand-600 bg-brand-50 rounded-card-sm px-2.5 py-1.5">
                점검 {inspectionIds.length}건 기반으로 생성됩니다.
              </p>
            )}
            {/* ⚠️ `!isEditMode`가 함께 있어야 한다 — 편집 URL(`?editReportId=`)은 siteId를 안 실어
                `!siteIdParam`만으로는 **편집 모드가 이 fallback에 빠진다**(2026-09-29 적발).
                그때 이 select는 «의미가 정의된 적 없는» 컨트롤이 된다:
                  · 문서의 현장 — 안 바뀐다(편집 저장은 `{ content }`만 보낸다, 아래 handleSubmit)
                  · 후보 rows   — 안 바뀐다(편집 로드 prefill은 `editLoadedRef`로 1회, 컨텍스트는
                                  문서 = `editReportPrefillContext`)
                  · 헤더 현장명 — **바뀐다**(`subtitle={sites.find(...)}`) → 화면이 「옮겼다」고 거짓말
                  · 새 등록 행 — **바뀐다**(`submitComposerCatalogRow(catalogKey, siteId, …)`)
                                  → 담당자를 새로 등록하면 **문서와 다른 현장**에 저장된다
                즉 바뀌는 것이 하필 틀린 둘이라, 노출 자체가 결함이었다.
                편집 모드의 현장은 문서(`report.siteId`)가 정본이고 로드가 이미 state에 넣는다
                (`setSiteId(report.siteId ?? '')`) — 감춰도 등록·제출이 그 값을 그대로 쓴다.
                `Report.siteId`는 schema에서 **non-nullable**이라 「편집인데 현장이 없어 제출이 막힌다」는
                막다른 골목은 생기지 않는다. 사용자 결정 2026-09-29(「감추는 쪽으로」). */}
            {!siteIdParam && !isEditMode && (
              <Card elevation="none">
                <label htmlFor="composed-site" className="block text-xs font-medium text-ink-600 mb-1">
                  현장 <span className="text-red-500">*</span>
                </label>
                <select
                  id="composed-site"
                  className="w-full px-3 py-2 border border-ink-300 rounded-control text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  value={siteId}
                  onChange={(e) => {
                    setSiteId(e.target.value);
                    setDirty(true);
                  }}
                >
                  <option value="">-- 현장 선택 --</option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </Card>
            )}

          {/* 작성 폼 — 래퍼 카드 없이 섹션을 페이지에 직접(구 SMS와 동일한 결, 2026-07-14 사용자 확정).
              섹션(group) 자체가 border+rounded 박스라 이중 카드가 시각 결을 갈랐음.
              렌더 게이트 — 그릴 것이 트리(저작/파생)에 있거나 편집형 list 선언이 있을 때만.
              종전 `nodes.length || dataSources || composerWidgetTree`와 등가다: `writeTree`가
              저작 트리(있을 때)이거나 파생 `nodes`이므로 앞 두 항이 그 하나로 합쳐진다. */}
          {(writeTree.length > 0 || (template?.dataSources?.length ?? 0) > 0) && (
            <div>
              {/* 작성 폼 렌더러는 하나다(2026-09-30 S3-b) — 트리의 출처만 둘(저작/파생, writeTree memo). */}
              <ComposerWidgetTreeRenderer
                tree={writeTree}
                values={values}
                onChange={handleChange}
                registeredWidgets={COMPOSED_ROW_WIDGETS}
                registeredFormWidgets={COMPOSED_FORM_WIDGETS}
                registeredWidgetContext={{ companyId, siteId }}
                dataSources={template?.dataSources}
                dataDefs={template?.dataDefs}
                resolvedRows={resolvedRows}
                // 현장 미선택이면 미배선 취급(액션 숨김) — 등록 대상 스코프(site)가 없다.
                onCatalogRowSubmit={siteId ? handleCatalogRowSubmit : undefined}
                onSuggestSearch={onSuggestSearch}
                onSuggestSelect={onSuggestSelect}
                // companyId 없으면 미배선 취급(photo 위젯 placeholder degrade) — 업로드 대상 조직이 없다.
                onPhotoUpload={companyId ? handlePhotoUpload : undefined}
                // 개선 이미지 AI 호스트(등록형 행 위젯 improvement-image) — companyId 없으면 undefined(표시 전용).
                onImprovementImage={improvementImageHost}
                // 서명 슬롯(P2) — 편집 모드에서만 배선(생성 모드는 undefined → 표시 전용).
                signatureSlots={slotSig.signatureSlots}
                initialValuesReady={initialValuesReady}
              />
            </div>
          )}

          </div>
        )}
      </ComposeWritePageShell>

      {/* 미저장 이탈 확인(뒤로가기 시). danger=취소(계속 작성)가 default 포커스 → 오조작 방지. */}
      <ConfirmModal
        open={leaveConfirmOpen}
        // 목적지가 있으면(형제 칩) 문구를 그 행동에 맞춘다 — "작성을 취소할까요?"는 다른
        // 문서로 옮기려는 사람에게는 무슨 일이 벌어지는지 잘못 알려 준다.
        title={pendingLeaveHref ? '다른 문서로 이동할까요?' : '작성을 취소할까요?'}
        body="저장되지 않은 변경사항이 있습니다. 나가면 사라집니다."
        confirmLabel={pendingLeaveHref ? '이동' : '나가기'}
        cancelLabel="계속 작성"
        danger
        onConfirm={confirmLeave}
        onClose={() => {
          setLeaveConfirmOpen(false);
          setPendingLeaveHref(null);
        }}
      />

      {/* 이어 작성 제안 — 작성 하나를 끝낸 직후 남은 작성 대기(CREATING) 문서로 건너뛸지 묻는다. 다음 대상을
          **라벨 인용으로** 부르는 것이 요점이다: 분할 축의 이름('공종' 등)은 템플릿마다 달라
          공용 문구에 박을 수 없고, 라벨을 인용하면 사용자는 어디로 가는지 정확히 알게 된다.
          저장은 이미 끝났고 어느 쪽을 골라도 잃는 게 없어 danger가 아니다(확인이 default
          포커스 — 이어 쓰는 것이 이 자리의 자연스러운 다음 행동이다). 자동 이동은 안 한다(§③). */}
      <ConfirmModal
        open={!!continueTarget}
        // 파생 층 문구는 도메인 중립 — "공종"은 TBM 어휘라 다른 양식이 분할을 쓰면 어긋난다
        // (2026-09-06 은닉/선언/파생 층 규율). 제목이 그룹 라벨을 인용해 목적지를 말하므로
        // body는 중복이라 두지 않는다.
        title={`「${continueTarget?.label ?? ''}」 문서를 이어 작성할까요?`}
        confirmLabel="이어 작성"
        cancelLabel="나중에"
        onConfirm={confirmContinueNext}
        onClose={declineContinueNext}
      />

      {/* 사진 AI 분석 덮어쓰기 확인(photoAnalysis 선언) — 그 행에 이미 입력된 위험요인이 있을 때만.
          문구는 구 TBM 생성 페이지 「위험요인 분석」 확인 모달 동형. danger 아님(구 primary 동형 —
          분석 시작이 자연스러운 다음 행동이고, 취소해도 업로드된 사진은 그대로다). */}
      <ConfirmModal
        open={photoAnalysis.confirmOpen}
        title="위험요인 분석"
        body="기존 위험요인/대책이 삭제되고 분석 결과로 대체됩니다. 계속하시겠습니까?"
        confirmLabel="분석 시작"
        cancelLabel="취소"
        onConfirm={photoAnalysis.confirmOverwrite}
        onClose={photoAnalysis.cancelOverwrite}
      />

      {/* 되돌리기 확인 — 입력값을 처음 상태로 복원. danger=취소가 default 포커스(오조작 방지). */}
      <ConfirmModal
        open={resetConfirmOpen}
        title="되돌리기"
        body="입력한 내용을 처음 상태로 되돌립니다."
        confirmLabel="되돌리기"
        cancelLabel="취소"
        danger
        onConfirm={handleReset}
        onClose={() => setResetConfirmOpen(false)}
      />

      {/* 서명 슬롯 패드(P2) — 서명 칸 탭 시 그 행 인물 이름을 제목에 띄운다(릴레이 수집:
          기기를 넘겨받은 사람이 자기 칸인지 확인). 확정 시 PUT → 서버 재조회로 셀 즉시 반영. */}
      <SignatureModal
        isOpen={!!slotSig.signRequest}
        onClose={slotSig.closeSignModal}
        title={
          slotSig.signRequest?.signerName
            ? `${slotSig.signRequest.signerName} 서명`
            : '서명'
        }
        onSubmit={slotSig.submitSignature}
        isSubmitting={slotSig.isSigning}
        error={slotSig.signError}
      />

      {/* 서명 지우기 확인(S13 재수집) — 증빙 삭제라 danger(취소 default 포커스). */}
      <ConfirmModal
        open={!!slotSig.deleteRequest}
        title="서명 지우기"
        body="이 칸의 서명을 지우고 다시 받을 수 있게 합니다."
        confirmLabel={slotSig.isDeleting ? '지우는 중...' : '지우기'}
        cancelLabel="취소"
        danger
        onConfirm={slotSig.confirmDelete}
        onClose={slotSig.cancelDelete}
      />
    </div>
  );
}
